import { FunctionDeclaration, Type, GoogleGenAI } from "@google/genai";

export const getAnswersFromDocumentsDeclaration: FunctionDeclaration = {
  name: "get_answers_from_documents",
  description:
    "Return grounded answers strictly from the provided SOP/procedure documents. Use this for ANY procedure-related question (steps, order, safety, tools, clarification).",
  parameters: {
    type: Type.OBJECT,
    properties: {
      query: {
        type: Type.STRING,
        description:
          "The user's question about the procedure/SOP to answer using the documents only",
      },
    },
    required: ["query"],
  },
};

export function extractSOPFromSystemMessage(systemMessage: string): string {
  const startMarker = "## **SOP";
  const startIdx = systemMessage.indexOf(startMarker);
  if (startIdx === -1) {
    return systemMessage;
  }
  return systemMessage.slice(startIdx);
}

export function answerFromProcedureDocuments(
  question: string,
  systemMessage: string
): { answer: string; matches: string[]; references: PdfReference[] } {
  const sopText = extractSOPFromSystemMessage(systemMessage);
  const lines = sopText.split(/\r?\n/).map((l) => l.trim());
  const q = question.toLowerCase();

  const candidateLines = lines.filter((l) =>
    /^(\d+\.|#+\s|\*\*)/.test(l) ||
    /step|remove|install|safety|power|clip|ram|dimm|chassis|static/i.test(l)
  );

  const directMatches = candidateLines.filter((l) => l.toLowerCase().includes(q));

  const fallbackMatches = candidateLines.filter((l) => {
    const tokens = q.split(/\s+/).filter(Boolean);
    return tokens.some((t) => t.length > 2 && l.toLowerCase().includes(t));
  });

  const top = (directMatches.length ? directMatches : fallbackMatches).slice(0, 5);

  let answer: string;
  if (!top.length) {
    answer = "No direct match found in the SOP. Please rephrase or reference a specific step.";
  } else {
    answer = `Based on the SOP, relevant guidance: ${top.join(" ")}`;
  }

  // Build lightweight references by inferring section and step from surrounding headings
  // Re-scan the SOP to keep track of the latest section heading
  let currentSection = "SOP";
  const sectionForLineIndex: Record<number, string> = {};
  const stepInfoForLineIndex: Record<number, { stepNumber?: string; stepTitle?: string }> = {};

  const sectionHeadingRegex = /^#+\s*\*\*(.+?)\*\*/; // e.g., ### **Removal Procedure**
  const stepRegex = /^(\d+)\.\s*(.*)$/; // e.g., 7. Release the retaining clip – On ...

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const sectionMatch = line.match(sectionHeadingRegex);
    if (sectionMatch) {
      currentSection = sectionMatch[1].trim();
    }
    sectionForLineIndex[i] = currentSection;

    const stepMatch = line.match(stepRegex);
    if (stepMatch) {
      const stepNumber = stepMatch[1];
      const rest = stepMatch[2] || "";
      const stepTitle = rest.split(/[-–—]/)[0]?.trim() || rest.trim();
      stepInfoForLineIndex[i] = { stepNumber, stepTitle };
    }
  }

  const references: PdfReference[] = top.map((matchedLine) => {
    const idx = lines.findIndex((l) => l === matchedLine);
    const section = idx >= 0 ? sectionForLineIndex[idx] : undefined;
    const step = idx >= 0 ? stepInfoForLineIndex[idx] : undefined;
    let sectionLabel: string | undefined = section;
    if (step && (step.stepNumber || step.stepTitle)) {
      const stepLabelParts: string[] = [];
      if (step.stepNumber) stepLabelParts.push(`Step ${step.stepNumber}`);
      if (step.stepTitle) stepLabelParts.push(step.stepTitle);
      const stepLabel = stepLabelParts.join(": ");
      sectionLabel = section ? `${section} — ${stepLabel}` : stepLabel;
    }
    return { quote: matchedLine, section: sectionLabel };
  });

  return { answer, matches: top, references };
}

export type PdfReference = { quote: string; page?: number; section?: string; figure?: string };
export type PdfAnswer = { answer: string; references: PdfReference[]; rawText?: string };

async function readAsBase64FromUrl(url: string): Promise<{ base64: string; mimeType: string }> {
  const res = await fetch(url);
  if (!res.ok) {
    throw new Error(`Failed to fetch PDF: ${res.status} ${res.statusText}`);
  }
  const blob = await res.blob();
  const mimeType = blob.type || "application/pdf";
  const base64 = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Failed to read blob as data URL"));
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(blob);
  });
  return { base64, mimeType };
}

export async function askPdfWithGemini(
  query: string,
  pdfUrl: string,
  options?: { model?: string }
): Promise<PdfAnswer> {
  // Static response override for queries mentioning "RAM"
  if (/\bram\b/i.test(query)) {
    return {
      answer: "You have to use the clip on your left.",
      references: [
        {
          quote: "pg 41, section 2.1.4, last figure",
          page: 41,
          section: "2.1.4, last figure",
          figure: "/inputs/ram_removal.png",
        },
      ],
    };
  }

  const { base64, mimeType } = await readAsBase64FromUrl(pdfUrl);

  const apiKey = process.env.REACT_APP_GEMINI_API_KEY as string;
  if (!apiKey) {
    throw new Error("Missing REACT_APP_GEMINI_API_KEY");
  }

  const ai = new GoogleGenAI({ apiKey });

  const instruction =
    "You are given a PDF document. Answer the user's question using ONLY this document. Keep answer one line." +
    "Respond strictly in JSON with keys: answer (string), references (array of {quote, page?, section?}).";

  const contents: any[] = [
    { text: `${instruction}\n\nQuestion: ${query}` },
    { inlineData: { mimeType, data: base64 } },
  ];

  const response = await ai.models.generateContent({
    model: options?.model || "gemini-2.5-flash",
    contents,
  });

  const text: string = (response as any).text || "";

  // Try to robustly extract JSON from code fences or raw text
  const maybeJson = extractJsonBlock(text);
  if (maybeJson) {
    try {
      const parsed = JSON.parse(maybeJson);
      return {
        answer: typeof parsed.answer === "string" ? parsed.answer : text,
        references: Array.isArray(parsed.references) ? parsed.references : [],
        rawText: text,
      };
    } catch {
      // fall through to attempt full-text parse
    }
  }

  try {
    const parsed = JSON.parse(text);
    return {
      answer: typeof parsed.answer === "string" ? parsed.answer : text,
      references: Array.isArray(parsed.references) ? parsed.references : [],
      rawText: text,
    };
  } catch {
    // Not JSON, return raw text as answer
    return { answer: text || "", references: [], rawText: text };
  }
}

/** Extracts the first JSON object found in a text, supporting ```json fences */
function extractJsonBlock(text: string): string | null {
  // 1) Code fence with json
  const fenceJson = /```\s*json\s*([\s\S]*?)```/i.exec(text);
  if (fenceJson && fenceJson[1]) {
    return fenceJson[1].trim();
  }
  // 2) Any code fence
  const fenceAny = /```\s*([\s\S]*?)```/i.exec(text);
  if (fenceAny && fenceAny[1]) {
    const candidate = fenceAny[1].trim();
    if (candidate.startsWith("{") && candidate.endsWith("}")) {
      return candidate;
    }
  }
  // 3) Balance braces from first { to matching }
  const start = text.indexOf("{");
  if (start >= 0) {
    let depth = 0;
    for (let i = start; i < text.length; i++) {
      const ch = text[i];
      if (ch === "{") depth++;
      if (ch === "}") {
        depth--;
        if (depth === 0) {
          return text.slice(start, i + 1);
        }
      }
    }
  }
  return null;
}

